import { useEffect, useState } from 'react';
import MediaQuery from 'react-responsive';
import ApiDatabase from 'server';
import Table from '../../../../../components/table/table';
import UseContext from '../../../../../hooks/useContext';
import Pagination from '../../../../../components/pagination/pagination';
import EmptyState from '../../../../../components/EmptyState/EmptyState';
import QualificationMobileCard from '../../../../../components/card/MobileListCard/QualificationMobileCard';
import useSlideOver from '../../../../../components/SlideOver/useSlideOver';
import { qualificationText } from '../../../../../components/SlideOver/dataSlideOver/data/slideOverQualification/qualificationTexts';

const TITLES = ['name', 'sector', 'tasks', 'createdAt'].map((key) =>
  qualificationText(`list.${key}`)
);

const MobileCardSkeleton = () => (
  <div className="flex flex-col gap-3 p-4">
    {[1, 2, 3, 4, 5].map((i) => (
      <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex-1">
            <div className="h-4 skeleton-bar w-3/4 mb-2" />
            <div className="h-3 skeleton-bar w-1/2" />
          </div>
          <div className="h-8 w-8 skeleton-bar rounded-lg" />
        </div>
        <div className="h-6 skeleton-bar rounded-md w-24 mb-3" />
        <div className="h-3 skeleton-bar w-32" />
      </div>
    ))}
  </div>
);

const TableSkeleton = () => (
  <table className="w-full">
    <tbody>
      {[1, 2, 3, 4, 5].map((i) => (
        <tr key={i}>
          {TITLES.map((_, idx) => (
            <td
              key={idx}
              className={`py-7 ${idx === 0 ? 'pl-4' : ''} ${idx === TITLES.length - 1 ? 'pr-3' : 'px-2'}`}
            >
              <div className="h-2 skeleton-bar" />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </table>
);

const BodyQualification = ({ search, optionSearch, showDisabled }) => {
  const { refreshQualification, setRefreshQualification, refreshQualifications, setObjID } = UseContext();
  const { toggleSlideOver } = useSlideOver();
  const [qualifications, setQualifications] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const limitElement = 20;
  const [totalPages, setTotalPages] = useState(0);
  const [countPerPage, setCountPerPage] = useState(0);
  const [countTotal, setCountTotal] = useState(0);
  const [isWaiting, setIsWaiting] = useState(true);

  const token = localStorage.getItem('xsrfToken');

  const fetchQualifications = () => {
    setIsWaiting(true);
    ApiDatabase.getAllQualifications(
      { token, search, limit: limitElement, page: currentPage, optionSearch, showDisabled: showDisabled === null ? 'null' : String(showDisabled) },
      (data) => {
        setQualifications(data.qualifications);
        setTotalPages(data.countPages);
        setCountPerPage(data.countPerPage);
        setCountTotal(data.count);
        setIsWaiting(false);
      },
      (err) => { if (err.name !== 'CanceledError') { setIsWaiting(false); console.error(err); } }
    );
  };

  useEffect(() => {
    setCurrentPage(1);
    const timer = setTimeout(fetchQualifications, 400);
    return () => clearTimeout(timer);
  }, [search, optionSearch, limitElement, refreshQualifications, showDisabled]);

  useEffect(() => {
    fetchQualifications();
  }, [currentPage]);

  useEffect(() => {
    if (refreshQualification && qualifications.length > 0) {
      setQualifications((prev) =>
        prev.map((q) =>
          q._id === refreshQualification._id || q.name === refreshQualification.name
            ? refreshQualification
            : q
        )
      );
      setRefreshQualification(false);
    }
  }, [refreshQualification]);

  const editQualification = (id) => {
    setObjID(id);
    toggleSlideOver('qualification');
  };

  return (
    <>
      <div className="flex flex-col w-full h-full overflow-hidden">
        <div className="flex-1 overflow-y-auto md:overflow-y-hidden">
          {isWaiting ? (
            <>
              <MediaQuery minWidth={768}><TableSkeleton /></MediaQuery>
              <MediaQuery maxWidth={767}><MobileCardSkeleton /></MediaQuery>
            </>
          ) : qualifications?.length > 0 ? (
            <>
              <MediaQuery minWidth={768}>
                <Table champsBody={qualifications} champsHeader={TITLES} />
              </MediaQuery>
              <MediaQuery maxWidth={767}>
                <div className="flex flex-col gap-3 p-4">
                  {qualifications.map((item, index) => (
                    <div
                      key={item._id}
                      className="motion-safe:animate-fadeUp"
                      style={{ animationDelay: `${Math.min(index, 9) * 30}ms` }}
                    >
                      <QualificationMobileCard item={item} onEdit={editQualification} />
                    </div>
                  ))}
                </div>
              </MediaQuery>
            </>
          ) : (
            <EmptyState type="qualification" />
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="flex-shrink-0 border-t border-gray-100 bg-white px-4 md:px-8 py-4">
          <Pagination
            currentPage={currentPage}
            countPerPage={countPerPage}
            countTotal={countTotal}
            totalPages={totalPages}
            setCurrentPage={setCurrentPage}
          />
        </div>
      )}
    </>
  );
};

export default BodyQualification;
